import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { MatFormField } from '@angular/material/form-field'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  ElderDataToolbarComponent,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToastService,
  ElderToolbarContentDirective,
  Filter,
  ISuggestionProvider,
  Pageable,
  SuggestionProvider,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { map } from 'rxjs/operators'
import { FilterBuilder } from '../../../orders/filter-builder'
import { WorkStation } from '../work-station'
import { WorkStationSelectDirective } from '../work-station-select/work-station-select.directive'
import { WorkStationService } from '../work-station.service'

@Component({
  selector: 'app-work-station-table',
  templateUrl: './work-station-table.component.html',
  styleUrls: ['./work-station-table.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatFormField,
    ElderSelectComponent,
    WorkStationSelectDirective,
    ElderSelectValueDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkStationTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly workStations$: BehaviorSubject<WorkStation[]> = new BehaviorSubject<
    WorkStation[]
  >([])

  public readonly suggestionProvider: ISuggestionProvider<WorkStation>

  @Input()
  public facilityId: string

  @Output()
  public workStationIdsChange: EventEmitter<string[]> = new EventEmitter<string[]>()

  private workStationService = inject(WorkStationService)

  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.suggestionProvider = SuggestionProvider.build((filter) =>
      this.workStationService
        .findAllPaged(new Pageable(0, 1000), [new Filter('query', filter)])
        .pipe(map((p) => p.content))
    )
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set workStationIds(ids: string[]) {
    if (ids && ids.length > 0) {
      this.workStationService
        .findAllPaged(
          new Pageable(0, 1000), // TODO Improve Page handling
          [FilterBuilder.ids(ids)]
        )
        .subscribe({
          next: (wss) => (this.workStations = wss.content),
          error: (err) => this.toastService.error('Failed to load WorkStations!', err),
        })
    } else {
      this.workStations = []
    }
  }

  public set workStations(workStations: WorkStation[]) {
    if (workStations) {
      this.workStations$.next(workStations)
    }
  }

  public get workStations(): WorkStation[] {
    return this.workStations$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onAdd(ws: WorkStation): void {
    if (ws) {
      this.add(ws)
    }
  }

  public onRemove(workStations: WorkStation[]): void {
    if (workStations) {
      this.remove(workStations)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private add(toAdd: WorkStation): void {
    const workStations = [...this.workStations]
    workStations.push(toAdd)
    this.workStations = workStations
    this.emitIds(this.workStations)
  }

  private remove(toRemove: WorkStation[]): void {
    const workStations = [...this.workStations]
    toRemove.forEach((c) => workStations.splice(workStations.indexOf(c), 1))
    this.workStations = workStations
    this.emitIds(this.workStations)
  }

  private emitIds(workStations: WorkStation[]): void {
    this.workStationIdsChange.emit(workStations.map((ws) => ws.id))
  }
}
